import type { AdminAuditAction, SubscriptionState } from '@odonix/contracts';
import { money } from '@odonix/core';

/**
 * Слова экранов платформенной админки. Состояние подписки читает не
 * владелец клиники, а сотрудник Odonix, поэтому формулировки короче и суше,
 * чем в `apps/webapp/src/features/billing/labels.ts`: здесь не объясняют
 * «что это значит для вас», а называют факт.
 */

export const STATE_LABEL: Record<SubscriptionState, string> = {
  trial: 'Пробный период',
  active: 'Активна',
  grace: 'Ожидает оплаты',
  restricted: 'Запись заблокирована',
  suspended: 'Приостановлена',
};

export const STATE_TONE: Record<SubscriptionState, 'info' | 'success' | 'warning' | 'danger'> = {
  trial: 'info',
  active: 'success',
  grace: 'warning',
  restricted: 'warning',
  suspended: 'danger',
};

export const KIND_LABEL: Record<'clinic' | 'lab', string> = {
  clinic: 'Клиника',
  lab: 'Лаборатория',
};

export const AUDIT_ACTION_LABEL: Record<AdminAuditAction, string> = {
  extend_trial: 'Продлил пробный период',
  activate: 'Перевёл в активную',
  manual_payment: 'Провёл оплату наличными',
  block: 'Заблокировал',
  restrict: 'Ограничил запись',
};

export function amount(minor: number): string {
  return money.format(money.kopecks(minor));
}

export function shortDate(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Подпись месяца для графика выручки: «март», без года — шесть соседних
 *  месяцев в один год почти всегда и не помещаются на узкую метку столбца. */
export function monthLabel(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU', { month: 'short', timeZone: 'UTC' });
}

/** Компактная форма суммы для чисел покрупнее: «880 тыс. ₽», «2,4 млн ₽».
 *  Полная сумма с копейками на такой цифре не читается быстрее, а места
 *  занимает вдвое больше — ровно то расстояние, ради которого экран открыли. */
export function amountCompact(minor: number): string {
  const rubles = minor / 100;
  if (Math.abs(rubles) >= 1_000_000) {
    return `${(rubles / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн ₽`;
  }
  if (Math.abs(rubles) >= 10_000) {
    return `${(rubles / 1_000).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} тыс. ₽`;
  }
  return amount(minor);
}

export function moment(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  pending: 'Ждём подтверждения',
  succeeded: 'Оплачено',
  canceled: 'Не прошло',
  failed: 'Не прошло',
};

export function paymentStatus(status: string, failureReason: string | null): string {
  const base = PAYMENT_STATUS_LABEL[status] ?? status;
  return failureReason === null ? base : `${base}: ${failureReason}`;
}

const PAYMENT_KIND_LABEL: Record<string, string> = {
  initial: 'Первый платёж',
  recurring: 'Автосписание',
  manual: 'Вручную',
};

export function paymentKind(kind: string): string {
  return PAYMENT_KIND_LABEL[kind] ?? kind;
}
